import { guestMode, type GuestMode } from './guestMode'
import { setGuestBase } from './guestPath'
import { acceptHandoff, collectHandoff, forwardUrl, handoffTarget } from './handoff'
import { resolveOldLink } from './oldLink'

// One guest app, two flows: temporary events under /t/<code>, permanent events under /p/<code>. The
// path picks the flow, on any host, before anything renders, and only that flow's code, CSS and <head>
// resources load. A bare /<code> is an old link: the API says which kind of event it is and the address
// is rewritten in place (same host, so the guest keeps their saved visitor code and sign-in). An old
// link that can't be settled (unknown code, failed call, no code) runs exactly as that host did before
// the merge: the permanent hosts in VITE_PERMANENT_HOSTS get the permanent flow, every other host the
// temporary one.

// One import() per flow, each its own call. Written as if/else, the minifier folds the two into one
// call before Vite fills in each import's CSS, so one flow loaded without its stylesheet (Vite 8.3.4,
// Rolldown 1.2.14: /p/ pages came up unstyled).
const flows: Record<GuestMode, () => Promise<unknown>> = {
  temporary: () => import('./temporary/main'),
  permanent: () => import('./permanent/main'),
}

function start(mode: GuestMode, prefix: string) {
  setGuestBase(prefix)
  void flows[mode]()
}

// MEMO-133: an old permanent host runs no flow; it hands the guest, with their saved state, to the one
// guest host. There, the handed-over state is stored before any flow reads storage.
const handoff = handoffTarget(window.location.hostname, import.meta.env.VITE_PERMANENT_HOSTS, import.meta.env.VITE_GUEST_ORIGIN)
let payload = ''
if (handoff) {
  try {
    payload = collectHandoff(localStorage)
  } catch {
    // storage blocked: forward without it
  }
} else {
  try {
    acceptHandoff(localStorage, document.referrer, import.meta.env.VITE_PERMANENT_HOSTS ?? '')
  } catch {
    // storage blocked: nothing to take
  }
}

const first = window.location.pathname.split('/')[1] ?? ''

if (handoff) window.location.replace(forwardUrl(handoff, window.location, payload))
else if (first === 't') start('temporary', '/t')
else if (first === 'p') start('permanent', '/p')
else {
  resolveOldLink(first).then((mode) => {
    if (!mode) {
      start(
        guestMode(
          window.location.hostname,
          import.meta.env.VITE_PERMANENT_HOSTS,
          import.meta.env.DEV ? import.meta.env.VITE_GUEST_MODE : undefined,
        ),
        '',
      )
      return
    }
    const prefix = mode === 'temporary' ? '/t' : '/p'
    const { pathname, search, hash } = window.location
    window.history.replaceState(window.history.state, '', prefix + pathname + search + hash)
    start(mode, prefix)
  })
}
